/** @prettier */

import {SnackbarProvider, useSnackbar} from 'notistack';
import {useState} from 'react';
import {useParams} from 'react-router-dom';
import type {LoaderFunctionArgs} from 'react-router-dom';
import {Container, Box} from '@mui/material';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import {useTranslation} from 'react-i18next';

//MTH component import
import {PrimaryButton} from '../../component/BasicButton';
//API import
import {
  postCommandsSites,
  getCompositionsGateway,
} from '../../API/API_Collection';
//interface import
import * as apiIF from '../../Type/api';
//Style import
import '../../css/common/common.css';

import {checkUnitExistence} from '../../functions/checkUnitExistence';
import type {unitExistence} from '../../functions/checkUnitExistence';

export async function loader({
  params,
}: LoaderFunctionArgs): Promise<boolean | undefined> {
  //パスパラメータを取得できない場合
  if (params.siteID === undefined) {
    return undefined;
  }

  const promises = [];
  //熱源機の登録状況を取得
  promises.push(checkUnitExistence(params.siteID));
  //ゲートウェイの情報を取得
  promises.push(
    getCompositionsGateway(params.siteID, {
      'fields': 'billing-status,system',
      'max-result': 100,
    })
  );

  try {
    const response = await Promise.all(promises);
    const heatSourceUnit = response[0] as unitExistence | undefined;
    const allGWInfo = response[1] as apiIF.res.getCompositionsGateway;

    if (heatSourceUnit === undefined) {
      return undefined;
    }
    //全ての熱源機ユニットが未登録ならリダイレクト
    const unregistered = Object.values(heatSourceUnit).every(value => {
      return value === false;
    });
    if (unregistered) {
      throw new Error(
        'Required equipment not registered or insufficient permission'
      );
    }

    if (allGWInfo === undefined) {
      return undefined;
    }
    //課金済みRT設定ゲートウェイが存在していない場合はリダイレクト
    const unpaid = allGWInfo['gateway-informations'].some(gwInfo => {
      return gwInfo['billing-status'] !== 'unpaid' && gwInfo['system'] === 'rt';
    });
    if (unpaid === false) {
      throw new Error('Billing status does not exist');
    }

    return true;
  } catch {
    return undefined;
  }
}

/**
 * 日報要求
 * @returns JSX.Element
 */
const RequestReport = () => {
  const trans = useTranslation().t; //翻訳関数
  const {enqueueSnackbar, closeSnackbar} = useSnackbar();

  //パスパラメータ取得
  const {siteID} = useParams();

  const [apiWait, setAPIWait] = useState(false); //API応答待ちフラグ

  /**
   * Requestボタン押下時処理
   * @returns
   */
  const requestReport = () => {
    //パスパラメータを取得できない場合
    if (siteID === undefined) {
      return;
    }

    setAPIWait(true);

    /** 現在時刻 */
    const now = new Date();
    /** リクエストボディ */
    const body: apiIF.reqParameters.postCommandsSitesBody = {
      'date': now.toISOString(),
      'states-report': 'request',
    };

    postCommandsSites(siteID, body, {mode: 'report'})
      .then(() => {
        const action = () => (
          <IconButton
            onClick={() => {
              closeSnackbar();
            }}>
            <CloseIcon className="serviceInfo__transferRequest__snackbar" />
          </IconButton>
        );
        const variant = 'success';
        enqueueSnackbar(trans('RequestReport.snackbar.success'), {
          variant,
          action,
        });
      })
      .finally(() => {
        setAPIWait(false);
      });
  };

  return (
    <>
      <Container fixed>
        <Box className="serviceInfo__title">
          <h1>{trans('RequestReport.title')}</h1>
          <h2>{trans('RequestReport.instruction')}</h2>
        </Box>
        <PrimaryButton
          onClick={requestReport}
          buttonText={trans('RequestReport.buttonTxt.request')}
          id="mv-transferRequestBtn"
          disabled={apiWait}
          apiWait={apiWait}
        />
      </Container>
    </>
  );
};
const Main = () => {
  //SnackbarProviderはuseSnackbar()と同じコンポーネントにできないため分割した
  return (
    <SnackbarProvider
      anchorOrigin={{vertical: 'top', horizontal: 'center'}}
      autoHideDuration={5000}>
      <RequestReport />
    </SnackbarProvider>
  );
};
export default Main;
